import React, { FC } from "react";

import { useTranslation } from "react-i18next";

import { CurrencyFormat, DateFormat } from "@/components/atoms";
import { Card, InsurerLinkDropdown } from "@/components/molecules";
import { CardInfo } from "@/components/molecules/Cards/Card/components/CardInfo.interface";
import { defaultDateFormat, formatDueDate } from "@/core/date";
import { Contract, Permissions } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";
import { getInsurerLinks } from "@/shared/utils";

import { getStatusColor } from "../../utils";
import { ContractImage } from "../ContractImage/ContractImage";

type ContractItemProps = {
    contract: Contract;
    simplified?: boolean;
    forceOwnerDisplay?: boolean;
    onClick?: () => void;
};

const CANCELLED_STATUS_LIST = ["2", "3"];

const ContractItem: FC<ContractItemProps> = ({ contract, simplified = false, onClick, forceOwnerDisplay = false }) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { navToContractDetail } = useNavigation();

    const contractCategoryId = !simplified && contract?.categories && contract?.categories[0]?.id;
    const insurerLinks = getInsurerLinks(contract);
    const canViewDetails = user.hasPermission(Permissions.ViewContractsDetails, contract.policyholderId);

    const handleContractItemClick = () => {
        return onClick ? onClick() : canViewDetails && navToContractDetail(contract);
    };

    const statusColor = getStatusColor(contract.state.id);

    const infos: Array<CardInfo | React.ReactElement> = [];

    infos.pushIfDefined(
        contract?.warrantiesList &&
            contract.warrantiesList.length > 0 && {
                title: t("Common.Warranties"),
                content: contract.warrantiesList.map((warranty) => warranty.warranty.name).join(", "),
            },
    );

    if (!simplified) {
        infos.pushIfDefined(
            contract.risks &&
                contract.risks.length > 0 && {
                    title: contract.risks[0]?.type.name,
                    content: contract.risks.map((risk) => risk.name).join(", "),
                },
        );

        infos.pushIf(contract.productCode, { title: t("Common.CompanyProduct"), content: contract.productCode });
        infos.push(
            { title: t("Common.Amount"), content: <CurrencyFormat value={contract.amount} /> },
            { title: t("Common.Periodicity"), content: contract.periodicity?.name },
            { title: t("Common.EffectiveDate"), content: <DateFormat date={contract.effectiveDate} /> },
        );

        infos.pushIf(
            !!insurerLinks.length,
            <InsurerLinkDropdown
                insurerLinks={insurerLinks}
                id={contract.id}
                insurer={contract.insurer}
                key={`insurer_link-${contract.id}`}
            />,
        );
    }

    const insurerFsma = contract.insurer?.fsma ? `(${contract.insurer?.fsma})` : "";

    const chip = contract.anotherBroker && {
        label: contract.anotherBroker.id,
        tooltip: contract.anotherBroker.name,
    };

    const isCancelledStatus = CANCELLED_STATUS_LIST.includes(contract.state?.id);

    const textBeforeDate = isCancelledStatus ? t("Common.EndDate") : t("Common.DueDate");

    const date = isCancelledStatus
        ? defaultDateFormat(contract.expirationDate, "dd.MM")
        : formatDueDate(contract.dueDate);
    return (
        <Card
            key={contract.id}
            forceOwnerDisplay={forceOwnerDisplay}
            onClickCard={handleContractItemClick}
            className="contractItem"
            dataTestId={`contract_item-card-${contract.id}`}
            clickable={canViewDetails}
            owner={contract.policyHolder}
            statusText={contract.state && t(contract.state.name)}
            date={!simplified && date}
            textBeforeDate={textBeforeDate}
            statusColor={statusColor}
            title={contract.branch.name}
            reference={`Ref ${contract.reference}`}
            infos={infos}
            chip={chip}
            companyName={`${contract.insurer.name}${insurerFsma}`}
            companyIcon={contract.insurer.logoUrl}
            categoryIcon={<ContractImage categoryId={contractCategoryId} />}
        />
    );
};

export default ContractItem;
